import React, { useCallback, useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Plus, TrendingUp, TrendingDown, Settings, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const MOVE_LABELS = { in: "Entrada", out: "Saída", adjust: "Ajuste (definido)", delta: "Ajuste (+/−)" };
const REASONS = ["Inventário / contagem", "Perda", "Avaria", "Vencimento", "Uso interno", "Correção de lançamento"];
const EMPTY_MOVE = { product_id: "", quantity: "", type: "adjust", reason: "" };

function stockBadge(stock, minStock) {
  if (stock < minStock) return <Badge variant="destructive">Baixo</Badge>;
  if (stock < minStock * 2) return <Badge className="bg-amber-500">Atenção</Badge>;
  return <Badge className="bg-emerald-600">OK</Badge>;
}

function moveIcon(type) {
  if (type === "in") return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (type === "out") return <TrendingDown className="w-4 h-4 text-rose-600" />;
  return <Settings className="w-4 h-4 text-slate-400" />;
}

export default function Stock() {
  const [products, setProducts] = useState([]);
  const [moves, setMoves] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_MOVE);

  const load = useCallback(async () => {
    const [p, m] = await Promise.all([api.get("/products"), api.get("/stock/movements")]);
    setProducts(p.data.filter((x) => x.type === "product"));
    setMoves(m.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdjust = (p) => { setForm({ ...EMPTY_MOVE, product_id: p?.id || "", quantity: p ? p.stock : "" }); setOpen(true); };
  const selected = products.find((p) => p.id === form.product_id);
  const preview = () => {
    if (!selected || form.quantity === "") return null;
    const q = Number(form.quantity);
    if (!Number.isFinite(q)) return null;
    const next = { in: selected.stock + q, out: selected.stock - q, adjust: q, delta: selected.stock + q }[form.type];
    return `${selected.stock} → ${next}`;
  };

  const save = async () => {
    if (!form.product_id || form.quantity === "") return toast.error("Selecione produto e quantidade");
    if ((form.type === "adjust" || form.type === "delta") && !form.reason.trim()) return toast.error("Informe o motivo do ajuste");
    try {
      await api.post("/stock/movements", { ...form, quantity: Number(form.quantity) });
      setOpen(false);
      setForm(EMPTY_MOVE);
      toast.success("Movimento registrado");
      // Defer refetch until Radix Dialog has fully unmounted (prevents removeChild race)
      setTimeout(load, 350);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const low = products.filter((p) => p.stock < p.min_stock);
  const total = products.reduce((s, p) => s + p.stock * p.cost_price, 0);

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Inventário</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Estoque</h1>
        </div>
        <Button onClick={() => openAdjust(null)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-move-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajuste / Movimento
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Itens únicos</div>
          <div className="kpi-num text-3xl mt-2">{products.length}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Valor total (custo)</div>
          <div className="kpi-num text-3xl mt-2 text-emerald-600">{brl(total)}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Estoque baixo</div>
          <div className="kpi-num text-3xl mt-2 text-rose-600 flex items-center gap-2">
            {low.length} <AlertTriangle className="w-6 h-6" />
          </div>
        </CardContent></Card>
      </div>

      {low.length > 0 && (
        <Card className="card-outline border-rose-200 bg-rose-50/50">
          <CardContent className="p-5">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2 text-rose-700"><AlertTriangle className="w-4 h-4" /> Alertas de estoque baixo</h3>
            <div className="flex flex-wrap gap-2">
              {low.map((p) => <Badge key={p.id} variant="destructive">{p.name} • {p.stock}/{p.min_stock}</Badge>)}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Produto</TableHead><TableHead>Categoria</TableHead>
              <TableHead className="text-right">Estoque</TableHead>
              <TableHead className="text-right">Mínimo</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.category || "—"}</TableCell>
                  <TableCell className="text-right kpi-num">{p.stock}</TableCell>
                  <TableCell className="text-right kpi-num text-slate-500">{p.min_stock}</TableCell>
                  <TableCell className="text-right kpi-num">{brl(p.stock * p.cost_price)}</TableCell>
                  <TableCell className="text-right">
                    {stockBadge(p.stock, p.min_stock)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openAdjust(p)} data-testid={`adjust-stock-${p.id}`}>
                      <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Ajustar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="card-outline">
        <CardContent className="p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Settings className="w-4 h-4" /> Últimos movimentos</h3>
          <div className="space-y-2">
            {moves.slice(0, 10).map((m) => (
              <div key={m.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 text-sm">
                <div className="flex items-center gap-2">
                  {moveIcon(m.type)}
                  <span className="font-medium">{m.product_name}</span>
                  <Badge variant="outline" className="text-[10px]">{MOVE_LABELS[m.type] || m.type}</Badge>
                  {m.reason && <span className="text-xs text-slate-500">{m.reason}</span>}
                </div>
                <div className="text-right">
                  <div className="kpi-num">{m.new_stock != null ? `${m.previous_stock} → ${m.new_stock}` : m.quantity}</div>
                  <div className="text-xs text-slate-500">{dateBR(m.created_at)}</div>
                </div>
              </div>
            ))}
            {moves.length === 0 && <div className="text-slate-400 text-sm text-center py-4">Nenhum movimento ainda</div>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader><DialogTitle className="font-display">Ajuste / Movimento de Estoque</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Produto</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="move-product"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.stock})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="move-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjust">Ajuste — informar a quantidade correta</SelectItem>
                  <SelectItem value="delta">Ajuste — somar ou subtrair (use − para tirar)</SelectItem>
                  <SelectItem value="in">Entrada</SelectItem>
                  <SelectItem value="out">Saída</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{form.type === "adjust" ? "Quantidade correta em estoque" : form.type === "delta" ? "Quantidade a somar (+) ou subtrair (−)" : "Quantidade"}</Label>
              <Input type="number" step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="move-qty" />
              {preview() && <div className="text-xs text-slate-600 mt-1" data-testid="move-preview">Estoque: <b className="kpi-num">{preview()}</b></div>}
            </div>
            <div><Label>Motivo{form.type === "adjust" || form.type === "delta" ? " *" : ""}</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="move-reason" placeholder="Descreva o motivo" />
              <div className="flex flex-wrap gap-1 mt-2">
                {REASONS.map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, reason: r })} data-testid={`reason-${r}`}
                    className={"text-[11px] px-2 py-1 rounded-full border transition " + (form.reason === r ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-400")}>{r}</button>
                ))}
              </div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-move-btn">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
